import React, { useEffect, useState } from "react";
import { Eye, EyeOff, Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

/**
 * Iter90 — Delivery-man login notebook (admin only, Indane agencies).
 *
 * Shown as a 6th tab inside the Delivery Dashboard modal. **View-only
 * from Iter94** — editing happens on the central `/delivery-logins`
 * page. Iter95 further strips the "Open Delivery Logins page" CTA
 * since the sidebar/home tile already give that route. Admin JWT
 * required for the backend GET.
 */
export default function DeliveryLoginTab({ groupId, agencyName }) {
  const [loading, setLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [savedAt, setSavedAt] = useState("");
  const [savedBy, setSavedBy] = useState("");
  const [hasRecord, setHasRecord] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/delivery-credentials/${groupId}`);
        if (cancelled) return;
        const c = data?.credentials;
        if (c) {
          setUsername(c.username || "");
          setPassword(c.password || "");
          setSavedAt(c.updated_at || "");
          setSavedBy(c.updated_by || "");
          setHasRecord(true);
        } else {
          setUsername("");
          setPassword("");
          setSavedAt("");
          setSavedBy("");
          setHasRecord(false);
        }
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [groupId]);

  const copyField = async (value, label) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 animate-spin text-[#385C4B]" />
      </div>
    );
  }

  const fmtWhen = () => {
    if (!savedAt) return "";
    try {
      const d = new Date(savedAt);
      return d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        timeZone: "Asia/Kolkata",
      });
    } catch {
      return savedAt;
    }
  };

  return (
    <div
      className="max-w-xl mx-auto p-4"
      data-testid="delivery-login-panel"
    >
      <div className="bg-white border border-[#DCDCD3] rounded-lg p-5 shadow-sm">
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-[#1C1C19]">
            🔐 Delivery Man Login — {agencyName}
          </h3>
          <p className="text-xs text-[#6B6B61] mt-1 leading-snug">
            {hasRecord
              ? "Saved credentials (view-only). Edit karne ke liye Delivery Logins page kholo."
              : "Abhi tak koi credentials save nahi hue. Delivery Logins page se setup karo."}
          </p>
        </div>

        {hasRecord ? (
          <>
            <label className="block mb-3">
              <span className="block text-[11px] font-mono uppercase tracking-wider text-[#6B6B61] mb-1">
                Username
              </span>
              <div className="flex items-center gap-2">
                <div
                  className="flex-1 px-3 py-2 text-sm border border-[#DCDCD3] rounded font-mono bg-[#F7F6EE] text-[#1C1C19] select-all"
                  data-testid="delivery-login-username"
                >
                  {username}
                </div>
                <button
                  type="button"
                  onClick={() => copyField(username, "Username")}
                  disabled={!username}
                  data-testid="delivery-login-copy-username"
                  className="p-2 border border-[#DCDCD3] rounded hover:bg-[#F7F6EE] disabled:opacity-40"
                  title="Copy username"
                >
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
            </label>

            <label className="block mb-4">
              <span className="block text-[11px] font-mono uppercase tracking-wider text-[#6B6B61] mb-1">
                Password
              </span>
              <div className="flex items-center gap-2">
                <div
                  className="flex-1 px-3 py-2 text-sm border border-[#DCDCD3] rounded font-mono bg-[#F7F6EE] text-[#1C1C19] select-all tracking-wider"
                  data-testid="delivery-login-password"
                >
                  {showPassword
                    ? password
                    : "•".repeat(Math.min(12, password.length || 8))}
                </div>
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  data-testid="delivery-login-toggle-password"
                  className="p-2 border border-[#DCDCD3] rounded hover:bg-[#F7F6EE]"
                  title={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <EyeOff className="w-3.5 h-3.5" />
                  ) : (
                    <Eye className="w-3.5 h-3.5" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => copyField(password, "Password")}
                  disabled={!password}
                  data-testid="delivery-login-copy-password"
                  className="p-2 border border-[#DCDCD3] rounded hover:bg-[#F7F6EE] disabled:opacity-40"
                  title="Copy password"
                >
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
            </label>

            {savedAt && (
              <p
                className="text-[10px] font-mono text-[#6B6B61] mb-3"
                data-testid="delivery-login-saved-meta"
              >
                Last updated: {new Date(savedAt).toLocaleString("en-IN", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                  timeZone: "Asia/Kolkata",
                })} {savedBy ? `· by ${savedBy}` : ""}
              </p>
            )}
          </>
        ) : (
          <p className="text-xs font-mono text-[#6B6B61] py-6 text-center">
            Koi credentials save nahi hain.
          </p>
        )}
      </div>
    </div>
  );
}